import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

import {
  paginationRequestSchema,
  pageNumberSchema,
  pageSizeSchema,
  type PaginationRequest,
} from '#packages/common/utils/pagination';

const PAGE_NUMBER_PARAM = 'page_number';
const PAGE_SIZE_PARAM = 'page_size';

export const usePagination = (defaults: PaginationRequest) => {
  const [searchParams, setSearchParams] = useSearchParams({
    page_size: defaults.pageSize.toString(),
    page_number: defaults.pageNumber.toString(),
  });

  const setPageNumber = useCallback(
    (newPageNumber: number) => {
      setSearchParams((prev) => {
        prev.set(
          PAGE_NUMBER_PARAM,
          pageNumberSchema.parse(newPageNumber).toString(),
        );
        return prev;
      });
    },
    [setSearchParams],
  );
  const goToPage = setPageNumber;

  const nextPage = useCallback(() => {
    setSearchParams((prev) => {
      const currentPageNumber = pageNumberSchema.parse(
        prev.get(PAGE_NUMBER_PARAM),
      );
      prev.set(PAGE_NUMBER_PARAM, (currentPageNumber + 1).toString());
      return prev;
    });
  }, [setSearchParams]);

  const previousPage = useCallback(() => {
    setSearchParams((prev) => {
      const currentPageNumber = pageNumberSchema.parse(
        prev.get(PAGE_NUMBER_PARAM),
      );

      // Prevent going to page 0 or negative pages
      if (currentPageNumber === 1) {
        return prev;
      }

      prev.set(
        PAGE_NUMBER_PARAM,
        Math.max(1, currentPageNumber - 1).toString(),
      );
      return prev;
    });
  }, [setSearchParams]);

  const setPageSize = useCallback(
    (newPageSize: number) => {
      setSearchParams((prev) => {
        prev.set(PAGE_SIZE_PARAM, pageSizeSchema.parse(newPageSize).toString());
        return prev;
      });
    },
    [setSearchParams],
  );

  const { pageNumber, pageSize } = paginationRequestSchema.parse({
    pageNumber: parseInt(searchParams.get(PAGE_NUMBER_PARAM) ?? 'NaN'),
    pageSize: parseInt(searchParams.get(PAGE_SIZE_PARAM) ?? 'NaN'),
  });

  return {
    pageNumber,
    pageSize,

    setPageNumber,
    setPageSize,

    nextPage,
    previousPage,
    goToPage,
  };
};
